import { Fragment, useMemo } from 'react';
import {
  Banner,
  Panel,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Text,
  Timeline,
  TimelineItem,
  Tooltip,
} from '@customerio/pluma-components/react';
import { formatDateTime } from '@customerio/pluma-components/react/utils/formatted-date-time';
import { channelValueOf, formatShortCalendar, getDeliveryChannel } from '@/features/deliveries';
import type { Delivery } from '@/services/db/delivery/entity';
import { buildDeliveryTimelineEvents, type DeliveryTimelineEvent } from '../utils/delivery-timeline-events';

type DeliveryMetricsTimelineProps = { delivery: Delivery };

const MACHINE_METRIC_DESCRIPTION =
  'Human metrics remain unchanged until Customer.io receives a qualifying human engagement event.';

/**
 * The delivery's metric lifecycle as a timeline. Shown only for channels that
 * report metrics (email, push, sms, …); absent for channels like
 * `live_notification` that carry no `deliveryMetrics`.
 */
export function DeliveryMetricsTimeline({ delivery }: DeliveryMetricsTimelineProps) {
  const reportsMetrics = getDeliveryChannel(channelValueOf(delivery.action_type))?.deliveryMetrics != null;
  if (!reportsMetrics) {
    return null;
  }
  return <MetricsTimelinePanel delivery={delivery} />;
}

/**
 * When engagement is machine-attributed and no human engagement has happened
 * yet, the key of the last machine event — the banner explaining the
 * attribution renders right after it. Null once any human engagement exists.
 */
function machineBannerAfterKey(events: DeliveryTimelineEvent[]): string | null {
  if (events.some((event) => event.attribution === 'human')) {
    return null;
  }
  const lastMachineEvent = [...events].reverse().find((event) => event.attribution === 'machine');
  return lastMachineEvent?.key ?? null;
}

function MetricsTimelinePanel({ delivery }: DeliveryMetricsTimelineProps) {
  const events = useMemo(() => buildDeliveryTimelineEvents(delivery), [delivery]);
  const currentEventKey = events.at(-1)?.key;
  const bannerAfterKey = machineBannerAfterKey(events);

  return (
    <Panel mt="200" data-test-delivery-detail-metrics>
      <PanelHeader>
        <PanelTitle>Metrics timeline</PanelTitle>
      </PanelHeader>
      <PanelSection>
        <Timeline metaPosition="trailing" data-test-delivery-timeline>
          {events.map((event) => (
            <Fragment key={event.key}>
              <TimelineItem
                data-test-delivery-timeline-event={event.key}
                color={event.color}
                markerIcon={event.icon}
                isCurrent={event.key === currentEventKey}
                title={event.label}
                description={event.description}
                meta={
                  <Tooltip content={formatDateTime(event.timestamp * 1000, 'full_with_time')}>
                    <Text as="span" size="s" color="subtle">
                      {formatShortCalendar(event.timestamp)}
                    </Text>
                  </Tooltip>
                }
              />
              {event.key === bannerAfterKey ? (
                <TimelineItem data-test-delivery-timeline-machine-banner>
                  <Banner
                    variant="caution"
                    title="Counts as a machine metric"
                    description={MACHINE_METRIC_DESCRIPTION}
                  />
                </TimelineItem>
              ) : null}
            </Fragment>
          ))}
        </Timeline>
      </PanelSection>
    </Panel>
  );
}
